import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../store/store";
import {
  updatePreferences,
  fetchPreferences,
} from "../store/slices/settingsSlice";
import { updateBalance } from "../store/slices/metricsSlice";
import settingsService from "../services/settingsService";

const Settings: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { preferences, isLoading, error } = useSelector(
    (state: RootState) => state.settings
  );
  const { user } = useSelector((state: RootState) => state.auth);

  const [settings, setSettings] = useState({
    currency: "USD",
    timezone: "UTC",
    dateFormat: "MM/DD/YYYY",
    language: "en",
    emailNotifications: true,
    performanceAlerts: true,
    paymentAlerts: true,
  });

  const [saveStatus, setSaveStatus] = useState<
    "idle" | "saving" | "success" | "error"
  >("idle");

  useEffect(() => {
    dispatch(fetchPreferences());
  }, [dispatch]);

  useEffect(() => {
    if (preferences) {
      setSettings({
        currency: preferences.currency || "USD",
        timezone: preferences.timezone || "UTC",
        dateFormat: preferences.date_format || "MM/DD/YYYY",
        language: preferences.language || "en",
        emailNotifications: preferences.email_notifications !== false,
        performanceAlerts: preferences.performance_alerts !== false,
        paymentAlerts: preferences.payment_alerts !== false,
      });
    }
  }, [preferences]);

  const currencies = [
    { code: "USD", name: "US Dollar" },
    { code: "EUR", name: "Euro" },
    { code: "GBP", name: "British Pound" },
    { code: "JPY", name: "Japanese Yen" },
    { code: "CAD", name: "Canadian Dollar" },
    { code: "AUD", name: "Australian Dollar" },
  ];

  const timezones = [
    "UTC",
    "EST (Eastern Standard Time)",
    "PST (Pacific Standard Time)",
    "GMT (Greenwich Mean Time)",
    "CET (Central European Time)",
  ];

  const handleSave = async () => {
    setSaveStatus("saving");
    try {
      const updatedPreferences = {
        currency: settings.currency,
        timezone: settings.timezone,
        date_format: settings.dateFormat,
        language: settings.language,
        email_notifications: settings.emailNotifications,
        performance_alerts: settings.performanceAlerts,
        payment_alerts: settings.paymentAlerts,
      };

      await dispatch(updatePreferences(updatedPreferences)).unwrap();

      // Update local metrics balance if currency changed
      if (settings.currency !== preferences.currency) {
        dispatch(updateBalance(preferences.balance || 0));
      }

      setSaveStatus("success");
      setTimeout(() => setSaveStatus("idle"), 3000);
    } catch (err) {
      setSaveStatus("error");
      setTimeout(() => setSaveStatus("idle"), 3000);
    }
  };

  // Demo conversion rates (in real app, fetch from backend)
  const conversionRates = {
    USD: 1.0,
    EUR: 0.92,
    GBP: 0.79,
    JPY: 148.0,
    CAD: 1.36,
    AUD: 1.52,
  };

  const formatCurrency = (amount: number, currency: string) => {
    const symbols: { [key: string]: string } = {
      USD: "$",
      EUR: "€",
      GBP: "£",
      JPY: "¥",
      CAD: "C$",
      AUD: "A$",
    };

    return `${symbols[currency] || "$"}${amount.toFixed(2)}`;
  };

  // Update the convertAmount function in Settings.tsx
  const convertAmount = (
    amount: number,
    fromCurrency: string,
    toCurrency: string
  ) => {
    const rates: { [key: string]: number } = {
      USD: 1.0,
      EUR: 0.92,
      GBP: 0.79,
      JPY: 148.0,
      CAD: 1.36,
      AUD: 1.52,
    };

    const fromRate = rates[fromCurrency] || 1;
    const toRate = rates[toCurrency] || 1;
    return (amount / fromRate) * toRate;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-normal text-gray-900">Settings</h1>
          <p className="text-sm text-gray-600">
            Configure your AdSense preferences
          </p>
        </div>

        <div className="flex items-center space-x-3">
          {saveStatus === "saving" && (
            <span className="text-sm text-gray-600">Saving...</span>
          )}
          {saveStatus === "success" && (
            <span className="text-sm text-green-600">Saved successfully!</span>
          )}
          {saveStatus === "error" && (
            <span className="text-sm text-red-600">Failed to save</span>
          )}
          <button
            onClick={handleSave}
            disabled={isLoading || saveStatus === "saving"}
            className={`px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 ${
              isLoading || saveStatus === "saving"
                ? "opacity-50 cursor-not-allowed"
                : ""
            }`}
          >
            Save changes
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Settings Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Account Settings */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Account Settings
          </h3>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Currency
              </label>
              <select
                value={settings.currency}
                onChange={(e) =>
                  setSettings({ ...settings, currency: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                {currencies.map((currency) => (
                  <option key={currency.code} value={currency.code}>
                    {currency.code} - {currency.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                All financial amounts will be displayed in this currency
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Timezone
              </label>
              <select
                value={settings.timezone}
                onChange={(e) =>
                  setSettings({ ...settings, timezone: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                {timezones.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date Format
              </label>
              <select
                value={settings.dateFormat}
                onChange={(e) =>
                  setSettings({ ...settings, dateFormat: e.target.value })
                }
                className="w-full px-3 py-2 border border-gray-300 rounded"
              >
                <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                <option value="YYYY-MM-DD">YYYY-MM-DD</option>
              </select>
            </div>
          </div>
        </div>

        {/* Notification Settings */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Notification Preferences
          </h3>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Email notifications</p>
                <p className="text-sm text-gray-600">
                  Receive notifications via email
                </p>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.emailNotifications}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      emailNotifications: e.target.checked,
                    })
                  }
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Performance alerts</p>
                <p className="text-sm text-gray-600">
                  Get alerts for performance changes
                </p>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.performanceAlerts}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      performanceAlerts: e.target.checked,
                    })
                  }
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">Payment alerts</p>
                <p className="text-sm text-gray-600">
                  Notifications about payments
                </p>
              </div>
              <label className="inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.paymentAlerts}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      paymentAlerts: e.target.checked,
                    })
                  }
                  className="sr-only peer"
                />
                <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Demo Conversion */}
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-6">
        <h3 className="font-medium text-gray-900 mb-3">
          Currency Conversion Preview
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white p-4 rounded border border-blue-100">
            <p className="text-sm text-gray-600">
              $100 USD equals approximately:
            </p>
            <p className="text-lg font-medium text-gray-900 mt-2">
              {formatCurrency(convertAmount(100, "USD", "EUR"), "EUR")}
            </p>
          </div>
          <div className="bg-white p-4 rounded border border-blue-100">
            <p className="text-sm text-gray-600">
              $100 USD equals approximately:
            </p>
            <p className="text-lg font-medium text-gray-900 mt-2">
              {formatCurrency(convertAmount(100, "USD", "GBP"), "GBP")}
            </p>
          </div>
          <div className="bg-white p-4 rounded border border-blue-100">
            <p className="text-sm text-gray-600">
              $100 USD equals approximately:
            </p>
            <p className="text-lg font-medium text-gray-900 mt-2">
              {formatCurrency(convertAmount(100, "USD", "JPY"), "JPY")}
            </p>
          </div>
          <div className="bg-white p-4 rounded border border-blue-100">
            <p className="text-sm text-gray-600">
              $100 USD equals approximately:
            </p>
            <p className="text-lg font-medium text-gray-900 mt-2">
              {formatCurrency(convertAmount(100, "USD", "CAD"), "CAD")}
            </p>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-3">
          Conversion rates are approximate and updated daily. Actual rates may
          vary.
        </p>
      </div>

      {/* Current Settings Summary */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          Current Settings
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Selected Currency</p>
            <p className="text-lg font-medium text-gray-900">
              {settings.currency}
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Timezone</p>
            <p className="text-lg font-medium text-gray-900">
              {settings.timezone}
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Email Notifications</p>
            <p className="text-lg font-medium text-gray-900">
              {settings.emailNotifications ? "Enabled" : "Disabled"}
            </p>
          </div>
          <div className="p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-600">Account Role</p>
            <p className="text-lg font-medium text-gray-900">
              {user?.role === "admin" ? "Administrator" : "Publisher"}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Settings;
